import React from 'react';
import {
  ChevronDown,
  ChevronRight,
  Package,
  Truck,
  Calendar,
  User,
  Info,
  CheckCircle,
  AlertCircle,
  Clock
} from 'lucide-react';
import {
  POrderHistoryDto,
  calculateOrderCompletionPercentage
} from '../../../../types/purchaseOrderHistory';
import { purchaseOrderHistoryService } from '../../../../services/purchaseOrderHistoryService';
import { LineRow } from './LineRow';

interface OrderRowProps {
  order: POrderHistoryDto;
  isExpanded: boolean;
  expandedLines: Set<string>;
  onToggle: () => void;
  onToggleLine: (lineNo: number) => void;
  onShowDetails: () => void;
}

export const OrderRow: React.FC<OrderRowProps> = ({
  order,
  isExpanded,
  expandedLines,
  onToggle,
  onToggleLine,
  onShowDetails
}) => {
  const completionPercentage = calculateOrderCompletionPercentage(
    order.totalQuantity,
    order.totalDelivered
  );

  const isCompleted = purchaseOrderHistoryService.isOrderCompleted(order);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Order Header */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            {/* Expand/Collapse Icon */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>

            {/* Order Icon */}
            <div className={`p-2 rounded-lg ${isCompleted ? 'bg-green-100' : 'bg-blue-100'}`}>
              <Package className={isCompleted ? 'text-green-600' : 'text-blue-600'} size={24} />
            </div>

            {/* Order Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-3 mb-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {order.orderNoERP || `#${order.orderNo}`}
                </h3>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${purchaseOrderHistoryService.getStatusBadgeClass(
                    order.orderStatus
                  )}`}
                >
                  {order.orderStatusText}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                {order.supplierName && (
                  <span className="flex items-center">
                    <Truck className="mr-1" size={14} />
                    {order.supplierName} ({order.supplierNo})
                  </span>
                )}
                {order.orderDate && (
                  <span className="flex items-center">
                    <Calendar className="mr-1" size={14} />
                    {purchaseOrderHistoryService.formatDate(order.orderDate)}
                  </span>
                )}
                {order.warehouse && (
                  <span className="flex items-center">
                    <Package className="mr-1" size={14} />
                    {order.warehouse}
                  </span>
                )}
              </div>
            </div>

            {/* Summary Stats */}
            <div className="hidden md:flex items-center space-x-6 text-sm">
              <div className="text-center">
                <p className="text-gray-600">Linjer</p>
                <p className="font-semibold text-gray-900">{order.totalLines}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">Antall</p>
                <p className="font-semibold text-gray-900">
                  {order.totalDelivered} / {order.totalQuantity}
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-600">Transaksjoner</p>
                <p className="font-semibold text-gray-900">{order.totalTransactions}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="hidden lg:block w-32">
              <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                <span>Fremdrift</span>
                <span>{completionPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${
                    completionPercentage === 100 ? 'bg-green-500' : 'bg-blue-500'
                  }`}
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>

            {/* Details Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onShowDetails();
              }}
              className="px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors flex items-center"
              title="Vis detaljer"
            >
              <Info className="mr-1" size={16} />
              Detaljer
            </button>
          </div>
        </div>

        {/* Mobile summary - shown only on small screens */}
        <div className="md:hidden mt-3 pt-3 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600">Linjer</p>
              <p className="font-semibold text-gray-900">{order.totalLines}</p>
            </div>
            <div>
              <p className="text-gray-600">Antall</p>
              <p className="font-semibold text-gray-900">
                {order.totalDelivered} / {order.totalQuantity}
              </p>
            </div>
            <div>
              <p className="text-gray-600">Fremdrift</p>
              <p className="font-semibold text-gray-900">{completionPercentage}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Lines */}
      {isExpanded && order.lines.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700">
              Ordrelinjer ({order.lines.length})
            </h4>
          </div>
          <div className="divide-y divide-gray-200">
            {order.lines.map((line) => {
              const lineKey = `${order.id}-${line.lineNo}`;
              return (
                <LineRow
                  key={lineKey}
                  line={line}
                  isExpanded={expandedLines.has(lineKey)}
                  onToggle={() => onToggleLine(line.lineNo)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* No lines message */}
      {isExpanded && order.lines.length === 0 && (
        <div className="px-4 py-8 text-center text-gray-500 border-t border-gray-200">
          <Package className="mx-auto mb-2 text-gray-400" size={32} />
          <p className="text-sm">Ingen linjer funnet for denne ordren</p>
        </div>
      )}
    </div>
  );
};
